import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useIntakeWorkflow } from "../hooks/useIntakeWorkflow";
import { useOrganizations } from "../hooks/useOrganizations";
import { Stage } from "../types/workflow.types";
import ErrorBoundary from "../components/ErrorBoundary";
import ErrorNotification from "../components/ErrorNotification";
import SimilarRequestsSidebar from "../components/SimilarRequestsSidebar";
import {
  ChatbotHeader,
  MessageList,
  RequestTypeSelector,
  ConfirmationButtons,
  ChatInput,
  StructuredCaptureCard,
  EditableSummaryCard
} from "../components/FillyOnIris";
import PrioritySignalCard from "../components/FillyOnIris/PrioritySignalCard";
import { StructuredCaptureData } from "../components/FillyOnIris/StructuredCaptureCard";
import { setOrgId as setGlobalOrgId, setFieldMappings as setGlobalFieldMappings } from "../services/asanaService";
import "./ChatBot.css";

/**
 * Props interface for the FillyOnIris component
 */
interface FillyOnIrisProps {}

/**
 * FillyOnIris Component - Refactored
 * 
 * This is the main container component for the intake workflow.
 * It has been completely refactored from 800+ lines to ~100 lines by:
 * 
 * - Extracting business logic into useIntakeWorkflow hook
 * - Moving organization management to useOrganizations hook
 * - Breaking UI into focused sub-components
 * - Adding comprehensive error handling
 * - Implementing proper TypeScript types
 * 
 * The component now focuses solely on orchestrating the workflow
 * and rendering the appropriate UI based on the current state.
 */
const FillyOnIris: React.FC<FillyOnIrisProps> = () => {
  // Routing hooks
  const { orgId, portfolioId: urlPortfolioId } = useParams<{ orgId?: string; portfolioId?: string }>();
  const navigate = useNavigate();
  
  // Business logic hooks
  const workflow = useIntakeWorkflow();
  const organizations = useOrganizations();
  
  // Program options for the editable summary dropdown
  const [programOptions, setProgramOptions] = useState<string[]>([]);
  
  // Track whether prioritization check has been attempted for current finalJSON
  const prioritizationAttemptedRef = useRef<string | null>(null);

  // Keep workflow's agent config and Asana PAT in sync with the resolved org/portfolio config
  useEffect(() => {
    if (organizations.resolvedConfig?.agentConfig) {
      workflow.setAgentConfig(organizations.resolvedConfig.agentConfig);
    }
    const pat = organizations.resolvedConfig?.asanaConfig?.personalAccessToken;
    workflow.setAsanaPat(pat);
    // Set global org ID so backend can resolve credentials from Secrets Manager
    setGlobalOrgId(organizations.selectedOrg?.id);
    // Set global field mappings so all asanaService calls use org/portfolio field mappings
    setGlobalFieldMappings(organizations.resolvedConfig?.fieldMappings);
  }, [organizations.resolvedConfig?.agentConfig, organizations.resolvedConfig?.asanaConfig?.personalAccessToken, organizations.resolvedConfig?.fieldMappings]);

  /**
   * Trigger similar requests fetch when finalJSON becomes available
   * This ensures we have the correct projectId from the selected organization
   */
  useEffect(() => {
    console.log('🔍 SIMILAR REQUESTS DEBUG: useEffect triggered');
    console.log('📊 DEBUG - workflow.finalJSON exists:', !!workflow.finalJSON);
    console.log('📊 DEBUG - organizations.selectedOrganization:', organizations.selectedOrganization);
    console.log('📊 DEBUG - selectedOrganization keys:', organizations.selectedOrganization ? Object.keys(organizations.selectedOrganization) : 'null');
    console.log('📊 DEBUG - asanaProject value:', organizations.selectedOrganization?.asanaProject);
    console.log('📊 DEBUG - asanaProject type:', typeof organizations.selectedOrganization?.asanaProject);
    
    const projectId = organizations.selectedOption?.asanaProjectId || organizations.selectedOrganization?.asanaProject;
    if (workflow.finalJSON && projectId) {
      console.log('✅ COMPONENT: All conditions met - triggering similar requests fetch');
      console.log('🔍 COMPONENT: projectId being sent:', projectId);
      
      workflow.fetchSimilarRequests(
        workflow.finalJSON.problem_statement || '',
        workflow.finalJSON.assigned_team || '',
        workflow.finalJSON.program_name || '',
        projectId
      );
    } else {
      console.log('❌ COMPONENT: Conditions not met for similar requests fetch');
      if (!workflow.finalJSON) {
        console.log('  - Missing finalJSON');
      }
      if (!organizations.selectedOrganization) {
        console.log('  - Missing selectedOrganization');
      }
      if (!projectId) {
        console.log('  - Missing asanaProjectId');
      }
    }
  }, [workflow.finalJSON, organizations.selectedOption?.asanaProjectId, organizations.selectedOrganization?.asanaProject]);

  /**
   * Trigger prioritization check when entering Summarization stage.
   * At this point, the user has completed the StructuredCapture (selected team, request type,
   * entered annualized entitlement, etc.) so the finalJSON has the complete data.
   * Uses a ref to ensure we only call once per unique finalJSON (prevents infinite loops).
   */
  useEffect(() => {
    if (workflow.stage !== Stage.Summarization) return;
    if (!workflow.finalJSON) return;
    
    // Create a fingerprint for the current finalJSON to detect changes
    const fingerprint = workflow.finalJSON.title + '|' + workflow.finalJSON.annualized_entitlement;
    
    // Only run if we haven't already attempted for this exact finalJSON + entitlement combo
    if (prioritizationAttemptedRef.current === fingerprint) return;
    
    // Don't run if we already have a result or it's loading
    if (workflow.prioritizationResult || workflow.prioritizationLoading || workflow.prioritizationDismissed) return;
    
    console.log('📊 PRIORITIZATION TRIGGER: Summarization stage reached with complete data, starting check');
    prioritizationAttemptedRef.current = fingerprint;
    workflow.runPrioritizationCheck(workflow.finalJSON);
  }, [workflow.stage, workflow.finalJSON]);

  /**
   * Fetch program options from Asana enum config when entering Summarization stage
   */
  useEffect(() => {
    if (workflow.stage === Stage.Summarization && organizations.selectedPortfolio && programOptions.length === 0) {
      import('../services/asanaService').then(({ getEnumFieldConfig }) => {
        getEnumFieldConfig(false, organizations.selectedPortfolio).then(enumConfig => {
          const programs = Object.keys(enumConfig.program_name?.options || {}).filter(p => p.trim());
          if (programs.length > 0) {
            setProgramOptions(programs);
          }
        }).catch(err => console.warn('Failed to fetch program options:', err));
      });
    }
  }, [workflow.stage, organizations.selectedPortfolio, programOptions.length]);

  /**
   * Handles user message submission
   * Passes the selected portfolio to the workflow for context
   */
  const handleMessageSend = (message: string) => {
    workflow.handleMessage(message, organizations.selectedPortfolio);
  };

  /**
   * Handles confirmation for submission
   * Calls the workflow's submitToAsana with organization data
   */
  const handleConfirmSubmission = () => {
    workflow.submitToAsana(organizations.selectedPortfolio, organizations.availableOrganizations);
  };

  /**
   * Handles confirmation cancellation
   * Returns to intake stage for further editing
   */
  const handleCancelSubmission = () => {
    workflow.dispatch({ type: 'SET_STAGE', payload: Stage.Intake });
    workflow.addMessage('bot', "No problem! Let's continue refining your request. What would you like to add or change?");
  };

  /**
   * Handles structured capture completion (Phase 2 → Phase 3 Summarization)
   * Merges structured data into finalJSON and moves to Summarization for final review
   */
  const handleStructuredCaptureContinue = (data: StructuredCaptureData) => {
    // Set request type in workflow state
    workflow.dispatch({ type: 'SET_REQUEST_TYPE', payload: data.requestType });

    // Update the selected portfolio/org
    // data.selectedOrgId is an asanaProjectId — find the matching portfolio option key
    if (data.selectedOrgId) {
      const matchingOption = organizations.portfolioOptions?.find(
        (opt: any) => opt.asanaProjectId === data.selectedOrgId
      );
      if (matchingOption) {
        organizations.setSelectedPortfolio(matchingOption.key);
      } else {
        // Fallback: try using the value directly (legacy orgs)
        organizations.setSelectedPortfolio(data.selectedOrgId);
      }
    }

    // Merge structured capture data into finalJSON (including dynamic prompt values)
    if (workflow.finalJSON) {
      workflow.dispatch({ type: 'SET_FINAL_JSON', payload: {
        ...workflow.finalJSON,
        ...(data.promptValues || {}), // Dynamic prompt field values
        annualized_entitlement: String(data.annualizedEntitlement || data.promptValues?.annualized_entitlement || 0),
        impact_metrics: data.impactMetric || data.promptValues?.impact_metrics || '',
        target_year: data.targetYear,
        request_type: data.requestType === 'op' ? 'OP' : 'In-Year',
      }});
    }

    // Move to Summarization stage for final review + submit
    workflow.dispatch({ type: 'SET_STAGE', payload: Stage.Summarization });
  };

  /**
   * Handles going back from structured capture to conversation
   */
  const handleBackToChat = () => {
    workflow.dispatch({ type: 'SET_STAGE', payload: Stage.Intake });
    workflow.addMessage('bot', "Sure! What else would you like to add or change about your request?");
  };

  // Show loading screen while organizations are being loaded
  if (organizations.loading) {
    return (
      <div className="chatbot-container">
        {/* Animated background blobs */}
        <div className="chatbot-blob-1"></div>
        <div className="chatbot-blob-2"></div>
        
        <div className="chatbot-loading-screen">
          <div className="chatbot-loading-content">
            <div className="chatbot-loading-spinner">
              <div className="chatbot-friendly-spinner">
                <div className="chatbot-friendly-dot"></div>
                <div className="chatbot-friendly-dot"></div>
                <div className="chatbot-friendly-dot"></div>
              </div>
            </div>
            <h2 className="chatbot-loading-title">Loading Organizations...</h2>
            <p className="chatbot-loading-description">
              Please wait while we load your available portfolios and organization data.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('🚨 FILLY COMPONENT ERROR:', error, errorInfo);
      }}
      resetKeys={[workflow.sessionId]} // Reset error boundary when session changes
    >
      <div className="chatbot-container">
        {/* Animated background blobs */}
        <div className="chatbot-blob-1"></div>
        <div className="chatbot-blob-2"></div>

        {/* Header */}
        <ChatbotHeader
          stage={workflow.stage}
          availableOrganizations={organizations.availableOrganizations}
          selectedPortfolio={organizations.selectedPortfolio}
          onPortfolioChange={organizations.setSelectedPortfolio}
          organizationsLoading={organizations.loading}
          isLoadingState={workflow.isLoadingState}
          getLoadingMessage={workflow.getLoadingMessage}
          userAlias={workflow.messages[0]?.content.replace('Welcome ', '').replace('!', '') || 'User'}
          onBackToDashboard={orgId ? () => navigate(`/org/${orgId}/hub`) : () => navigate('/')}
          onTasksView={orgId ? () => navigate(`/org/${orgId}/tasks`) : undefined}
          onToggleView={orgId ? () => navigate(`/org/${orgId}/settings`) : undefined}
          onResetChat={workflow.resetChat}
          onHome={() => navigate('/')}
          orgId={orgId}
        />

        {/* Error Notification for Organizations */}
        {organizations.error && (
          <ErrorNotification
            error={organizations.error}
            type="warning"
            title="Organization Loading Error"
            onRetry={organizations.reloadOrganizations}
            onDismiss={() => organizations.setError(null)}
            retryable={true}
            position="inline"
          />
        )}

        {/* Chat Messages + Inline Structured Capture (all in one scrollable area) */}
        <MessageList
          messages={workflow.messages}
          loading={workflow.loading}
          isLoadingState={workflow.isLoadingState}
          getLoadingMessage={workflow.getLoadingMessage}
        >
          {/* Request Type Selection (legacy — kept for backward compatibility) */}
          {workflow.stage === Stage.RequestType && (
            <RequestTypeSelector
              selectedRequestType={workflow.selectedRequestType}
              onRequestTypeSelection={workflow.handleRequestTypeSelection}
              loading={workflow.loading}
              isLoadingState={workflow.isLoadingState}
            />
          )}

          {/* Retry button for failed messages */}
          {workflow.hasFailedMessage && !workflow.loading && !workflow.isLoadingState && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', animation: 'chatbot-fadeIn 0.3s ease-out' }}>
              <button
                type="button"
                onClick={workflow.retryLastMessage}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  background: '#fff3cd',
                  border: '1.5px solid #ffc107',
                  borderRadius: '20px',
                  color: '#856404',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                }}
                onMouseOver={e => { (e.target as HTMLElement).style.transform = 'translateY(-1px)'; (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
                onMouseOut={e => { (e.target as HTMLElement).style.transform = 'none'; (e.target as HTMLElement).style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)'; }}
              >
                🔄 Tap to retry
              </button>
            </div>
          )}

          {/* Structured Capture Card (Phase 2 — sequential inline questions) */}
          {workflow.stage === Stage.StructuredCapture && workflow.finalJSON && (
            <StructuredCaptureCard
              finalJSON={workflow.finalJSON}
              organizations={orgId 
                ? organizations.availableOrganizations.filter((o: any) => o.id === orgId)
                : organizations.availableOrganizations
              }
              selectedOrgId={organizations.selectedPortfolio || orgId || ''}
              fieldMappings={organizations.resolvedConfig?.fieldMappings}
              onContinue={handleStructuredCaptureContinue}
              onBackToChat={handleBackToChat}
              disabled={workflow.loading || workflow.isLoadingState}
            />
          )}

          {/* Priority Signal Card (shown during Summarization — before submit) */}
          {workflow.stage === Stage.Summarization && (workflow.prioritizationResult || workflow.prioritizationLoading) && (
            <PrioritySignalCard
              result={workflow.prioritizationResult}
              loading={workflow.prioritizationLoading}
              dismissed={workflow.prioritizationDismissed}
              onDismiss={workflow.dismissPrioritization}
            />
          )}

          {/* Editable Summary Card (Phase 3 — review & edit before submit) */}
          {/* Only show after priority assessment is done (result available or loading finished) */}
          {workflow.stage === Stage.Summarization && workflow.finalJSON && !workflow.loading && !workflow.isLoadingState && !workflow.prioritizationLoading && (
            <EditableSummaryCard
              finalJSON={workflow.finalJSON}
              organizationName={organizations.selectedOrganization?.name || 'Default'}
              selectedOrgId={organizations.selectedPortfolio || ''}
              organizations={organizations.availableOrganizations}
              programOptions={programOptions}
              onSubmit={(editedData, selectedAsanaProjectId) => {
                // Update finalJSON with any edits the user made
                workflow.dispatch({ type: 'SET_FINAL_JSON', payload: editedData });
                // Update selected portfolio if changed
                // selectedAsanaProjectId is an asanaProjectId from the dropdown
                if (selectedAsanaProjectId && selectedAsanaProjectId !== organizations.selectedPortfolio) {
                  const matchingOpt = organizations.portfolioOptions?.find(
                    (opt: any) => opt.asanaProjectId === selectedAsanaProjectId
                  );
                  if (matchingOpt) {
                    organizations.setSelectedPortfolio(matchingOpt.key);
                  }
                }
                // Submit to Asana with the asanaProjectId
                workflow.submitToAsana(selectedAsanaProjectId || organizations.selectedPortfolio, organizations.availableOrganizations);
              }}
              onBack={() => {
                workflow.dispatch({ type: 'SET_STAGE', payload: Stage.StructuredCapture });
              }}
              disabled={workflow.loading || workflow.isLoadingState}
            />
          )}
        </MessageList>

        {/* Edit Mode Banner */}
        {workflow.isEditMode && (
          <div className="chatbot-edit-mode-banner">
            <div className="chatbot-edit-mode-content">
              <div className="chatbot-edit-mode-icon">✏️</div>
              <div className="chatbot-edit-mode-text">
                <strong>Edit Mode</strong> - Making changes to your request
              </div>
              <div className="chatbot-edit-mode-actions">
                {workflow.showManualSummaryOption && (
                  <button
                    onClick={() => workflow.manualGenerateSummary(organizations.selectedPortfolio)}
                    className="chatbot-manual-summary-btn"
                    disabled={workflow.loading || workflow.isLoadingState}
                  >
                    Generate Summary
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manual Summary Option (when not in edit mode) */}
        {!workflow.isEditMode && workflow.showManualSummaryOption && workflow.stage === Stage.Intake && (
          <div className="chatbot-manual-summary-prompt">
            <div className="chatbot-manual-summary-content">
              <div className="chatbot-manual-summary-text">
                🎯 <strong>Ready to Review?</strong> I've gathered substantial information about your request.
              </div>
              <button
                onClick={() => workflow.manualGenerateSummary(organizations.selectedPortfolio)}
                className="chatbot-manual-summary-btn"
                disabled={workflow.loading || workflow.isLoadingState}
              >
                Generate Summary
              </button>
            </div>
          </div>
        )}

        {/* Chat Input */}
        {workflow.stage === Stage.Intake && (
          <ChatInput
            onSend={handleMessageSend}
            disabled={workflow.loading || workflow.isLoadingState}
            loading={workflow.loading || workflow.isLoadingState}
            placeholder={workflow.isEditMode ? "What would you like to change?" : "Describe your feature request..."}
          />
        )}

        {/* Success State */}
        {workflow.stage === Stage.Complete && !workflow.submissionError && (
          <div className="chatbot-success">
            <div className="chatbot-success-content">
              <div className="chatbot-success-badge">
                <div className="chatbot-success-dot"></div>
                <span className="chatbot-success-badge-text">Request Submitted</span>
              </div>
              <p className="chatbot-success-text">
                Thank you! Your feature request has been analyzed and submitted successfully.
              </p>
              {workflow.asanaTaskUrl && (
                <div className="chatbot-success-link">
                  <p>
                    🌐 <a 
                      href={workflow.asanaTaskUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{color: '#007bff', textDecoration: 'underline'}}
                    >
                      {workflow.asanaTaskUrl}
                    </a>
                  </p>
                </div>
              )}
              {workflow.finalJSON && (
                <div className="chatbot-success-details">
                  <p>
                    Assigned Team: <strong>{workflow.finalJSON.assigned_team}</strong> | 
                    Theme: <strong>{workflow.finalJSON.theme}</strong> | 
                    Program: <strong>{workflow.finalJSON.program_name}</strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submission Error State with Retry Options */}
        {workflow.stage === Stage.Complete && workflow.submissionError && (
          <div className="chatbot-error-recovery">
            <div className="chatbot-error-content">
              <div className="chatbot-error-badge">
                <div className="chatbot-error-dot"></div>
                <span className="chatbot-error-badge-text">Submission Failed</span>
              </div>
              
              <ErrorNotification
                error={workflow.submissionError.message}
                type="error"
                title="Asana Task Creation Failed"
                onRetry={workflow.submissionError.retryable ? 
                  () => workflow.retryAsanaSubmission(organizations.selectedPortfolio, organizations.availableOrganizations) : 
                  undefined
                }
                onDismiss={() => workflow.clearSubmissionError()}
                retryable={workflow.submissionError.retryable}
                dismissible={true}
                position="inline"
              />

              {/* Alternative Actions */}
              <div className="chatbot-error-actions">
                <h4>Your request details are preserved:</h4>
                {workflow.finalJSON && (
                  <div className="chatbot-preserved-details">
                    <p><strong>Team:</strong> {workflow.finalJSON.assigned_team}</p>
                    <p><strong>Program:</strong> {workflow.finalJSON.program_name}</p>
                    <p><strong>Portfolio:</strong> {organizations.selectedOrganization?.name || 'Default'}</p>
                  </div>
                )}
                
                <div className="chatbot-recovery-buttons">
                  {workflow.submissionError.retryable && (
                    <button
                      onClick={() => workflow.retryAsanaSubmission(organizations.selectedPortfolio, organizations.availableOrganizations)}
                      className="chatbot-retry-btn"
                      disabled={workflow.loading || workflow.isLoadingState}
                    >
                      🔄 Retry Submission
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      const exportResult = workflow.exportRequestData('json');
                      if (exportResult) {
                        const blob = new Blob([exportResult.data], { type: exportResult.mimeType });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = exportResult.filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }
                    }}
                    className="chatbot-export-btn"
                  >
                    💾 Save as JSON
                  </button>
                  
                  <button
                    onClick={() => {
                      const exportResult = workflow.exportRequestData('csv');
                      if (exportResult) {
                        const blob = new Blob([exportResult.data], { type: exportResult.mimeType });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = exportResult.filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }
                    }}
                    className="chatbot-export-btn"
                  >
                    📊 Save as CSV
                  </button>
                  
                  <button
                    onClick={() => {
                      const exportResult = workflow.exportRequestData('email');
                      if (exportResult) {
                        window.location.href = exportResult.data;
                      }
                    }}
                    className="chatbot-export-btn"
                  >
                    📧 Email to Team
                  </button>
                  
                  <button
                    onClick={workflow.resetChat}
                    className="chatbot-new-request-btn"
                  >
                    ➕ Start New Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Similar Requests Toggle Button */}
        {(workflow.stage === Stage.StructuredCapture || workflow.stage === Stage.Summarization || workflow.stage === Stage.Complete) && (
          <button
            onClick={workflow.toggleSidebar}
            className="chatbot-sidebar-toggle"
            aria-label="Toggle similar requests sidebar"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
              />
            </svg>
            <span>Similar Requests ({workflow.similarRequests?.count || 0})</span>
          </button>
        )}

        {/* Similar Requests Sidebar */}
        <SimilarRequestsSidebar 
          isOpen={workflow.sidebarOpen} 
          onToggle={workflow.toggleSidebar}
          similarRequestsData={workflow.similarRequests}
          loading={workflow.similarRequestsLoading}
        />
      </div>
    </ErrorBoundary>
  );
};

export default FillyOnIris;
